import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { create } from '@thoughtspot/logger';
import { Microfrontend, ReleaseJson } from '@thoughtspot/microfrontends';
import { loadCssDynamically } from '/@utils/browser.util';
import { getMfeDeliverySource } from './mfe-window-bridge';
import { MICRO_FRONTEND_DYNAMIC_IMPORT } from './microfrontends-config';

declare const window: any;

/** Origin of an MFE's resource URLs; reported as `mfe_source` on MFE_LOADED. */
export type MfeResourceSource = 'override' | 'manifest' | 'legacy' | 'bundled';

export type MfeResources = {
    mfeJsResourceUrl: string;
    mfeCssResourceUrl: string;
    mfeReleaseVersion: string;
    mfeBuildNumber: string;
    source: MfeResourceSource;
};

const logger = create('microfrontends-util');
const initializedMicroFrontends: Partial<Record<
    Microfrontend,
    Promise<any>
>> = {};

export const getMfeResourceUrls = async (
    mfeName: Microfrontend,
): Promise<MfeResources> => {
    const { queryParams } = getQueryParamsAndQueryString();
    let mfeJsResourceUrl: string;
    let mfeCssResourceUrl: string;
    let mfeReleaseVersion: string;
    let mfeBuildNumber: string;
    let source: MfeResourceSource = 'bundled';
    if (queryParams[`${mfeName}Mfe`]) {
        // fetch and parse release json
        try {
            const response = await fetch(queryParams[`${mfeName}Mfe`]);
            const releaseJson: ReleaseJson = await response.json();
            mfeJsResourceUrl =
                releaseJson?.resources?.js &&
                [
                    releaseJson.basePath,
                    releaseJson.app,
                    releaseJson.resources.js,
                ].join('/');
            mfeCssResourceUrl =
                releaseJson?.resources?.css &&
                [
                    releaseJson.basePath,
                    releaseJson.app,
                    releaseJson.resources.css,
                ].join('/');
            mfeReleaseVersion = releaseJson.baseVersion;
            mfeBuildNumber = releaseJson.buildTag.split('-')[1];
            source = 'override';
        } catch (error) {
            logger.error(`Failed to fetch release json for ${mfeName}`);
        }
    } else if (queryParams[`${mfeName}_js`] && queryParams[`${mfeName}_css`]) {
        // To be used by MFE CI
        // Ensure MFE Folder names do not have an underscore
        mfeJsResourceUrl = queryParams[`${mfeName}_js`];
        mfeCssResourceUrl = queryParams[`${mfeName}_css`];
        source = 'override';
    } else if (window?.blink?.microfrontends?.[mfeName]?.js) {
        mfeJsResourceUrl = window?.blink?.microfrontends?.[mfeName]?.js;
        mfeCssResourceUrl = window?.blink?.microfrontends?.[mfeName]?.css;
        mfeReleaseVersion =
            window?.blink?.microfrontends?.[mfeName].baseVersion;
        mfeBuildNumber = window?.blink?.microfrontends?.[
            mfeName
        ].buildTag?.split('-')[1];
        // The payload is the bootstrap's unless the registry replaced it.
        source = getMfeDeliverySource();
    }
    return {
        mfeJsResourceUrl,
        mfeCssResourceUrl,
        mfeReleaseVersion,
        mfeBuildNumber,
        source,
    };
};

const loadMicroFrontendModule = async (mfeName: Microfrontend) => {
    const {
        mfeJsResourceUrl,
        mfeCssResourceUrl,
        source,
    } = await getMfeResourceUrls(mfeName);
    if (mfeCssResourceUrl || mfeJsResourceUrl)
        logger.info(
            `Fetching JS and CSS resources (${source}) from ${mfeJsResourceUrl} and ${mfeCssResourceUrl}`,
        );
    loadCssDynamically(mfeJsResourceUrl && mfeCssResourceUrl);
    const mfe = mfeJsResourceUrl
        ? // eslint-disable-next-line
          await import(/* @vite-ignore */ mfeJsResourceUrl)
        : await MICRO_FRONTEND_DYNAMIC_IMPORT[mfeName]();
    logger.info(`MFE ${mfeName} initialized (${source})`);
    return mfe;
};

/** Memoises the in-flight import; a rejected load is evicted so the next render retries. */
export const initMicroFrontend = (mfeName: Microfrontend): Promise<any> => {
    if (!initializedMicroFrontends[mfeName]) {
        initializedMicroFrontends[mfeName] = loadMicroFrontendModule(
            mfeName,
        ).catch(error => {
            delete initializedMicroFrontends[mfeName];
            throw error;
        });
    }
    return initializedMicroFrontends[mfeName];
};

export const getMicroFrontendHook = async (
    mfeName: Microfrontend,
    hookName: any,
) => {
    const mfe = await initMicroFrontend(mfeName);
    return mfe[hookName];
};

export const getMicroFrontendComponent = async (
    mfeName: Microfrontend,
    componentName: any,
) => {
    const mfe = await initMicroFrontend(mfeName);
    return {
        default: mfe[componentName],
    };
};
